/* eslint-disable @typescript-eslint/no-explicit-any */
import type { GetContentOptions } from '@builder.io/sdk'

import { BUILDER_BLOG_MODEL, BUILDER_PAGE_MODEL, getUrlContentApi } from '@/legacy/config/builder'
import { builderRequest } from '@/legacy/core/request'
import { objectToQueryString } from '@/legacy/core/utils/toQueryString'
import type { BuilderPageModel, CardArticleEntryData } from '@/legacy/models'

import { sendError } from '../error'

interface GetPagesOptions extends Pick<GetContentOptions, 'query'> {
  model?: string
  page?: number
  perPage?: number
  fields?: string
  params?: {
    includeRefs?: boolean
    noTargeting?: boolean
  }
  sort?: { [key: string]: any }
}

export const getPages = async <T = BuilderPageModel>({
  model = BUILDER_PAGE_MODEL,
  page = 0,
  perPage = 30,
  params,
  ...options
}: GetPagesOptions) => {
  const { data } = await builderRequest.get<{ results: T[] }>(getUrlContentApi(model), {
    params: {
      limit: perPage + 1,
      offset: page * perPage,
      omit: 'data.blocks',
      ...params,
      ...objectToQueryString(options),
    },
  })

  return data?.results || []
}

export const getPage = async <T = BuilderPageModel>({
  model = BUILDER_PAGE_MODEL,
  ...options
}: Omit<GetPagesOptions, 'perPage' | 'page'>) => {
  const { data } = await builderRequest.get<{ results: T[] }>(getUrlContentApi(model), {
    params: {
      limit: 1,
      omit: 'data.blocks',
      ...objectToQueryString(options),
    },
  })

  return ((data?.results || []).shift() || {}) as T
}

interface GetArticlesOptions extends Pick<GetContentOptions, 'query'> {
  page: number
  perPage: number
  fields?: string
  sort?: { [key: string]: any }
}

export const getArticles = ({ page = 0, perPage = 30, ...options }: GetArticlesOptions) =>
  getPages<CardArticleEntryData>({
    model: BUILDER_BLOG_MODEL,
    page,
    perPage,
    params: {
      includeRefs: true,
      noTargeting: true,
    },
    ...options,
  })

export const getArticle = async ({ id }: Record<'id', string>) => {
  try {
    const { data } = await builderRequest.get<{ results: CardArticleEntryData[] }>(
      getUrlContentApi(BUILDER_BLOG_MODEL),
      {
        params: {
          id,
          fields: 'id,name,data.view',
        },
      },
    )
    return data.results[0] || null
  } catch (error) {
    sendError({ error })
  }

  return null
}
